// src/context/PermissionsContext.js

import React, { createContext, useContext, useState, useCallback } from "react";
import { getUserPermissions } from "shared/api/function_data";

const PermissionsContext = createContext(null);

// ─── Role → Permissions map (since API only returns role) ───
const ROLE_PERMISSIONS = {
  admin: {
    canView: true,
    canEdit: true,
    canUpload: true,
    canAdmin: true,
    canManageUsers: true,
    canDelete: true,
  },
  editor: {
    canView: true,
    canEdit: true,
    canUpload: true,
    canAdmin: false,
    canManageUsers: false,
    canDelete: false,
  },
  viewer: {
    canView: true,
    canEdit: false,
    canUpload: false,
    canAdmin: false,
    canManageUsers: false,
    canDelete: false,
  },
  no_access: {
    canView: false,
    canEdit: false,
    canUpload: false,
    canAdmin: false,
    canManageUsers: false,
    canDelete: false,
  },
};

export function PermissionsProvider({ children }) {
  const [role, setRole] = useState("no_access");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetched, setFetched] = useState(false);

  const fetchPermissions = useCallback(async (login, functionId) => {
    if (!login || !functionId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getUserPermissions(login, functionId,2026);
      setRole(data.role || "no_access");
      setFetched(true);
    } catch (err) {
      console.error("Failed to fetch permissions:", err);
      setError(err.message);
      setRole("viewer");
      setFetched(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const resetPermissions = useCallback(() => {
    setRole("no_access");
    setFetched(false);
    setError(null);
  }, []);

  // ─── Derive everything from role ───
  const perms = ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS.no_access;

  return (
    <PermissionsContext.Provider
      value={{
        role,
        loading,
        error,
        fetched,
        fetchPermissions,
        resetPermissions,

        // Derived from role
        isAdmin: role === "admin",
        isEditor: role === "editor",
        isViewer: role === "viewer",
        hasAccess: role !== "no_access",

        // Derived from ROLE_PERMISSIONS map
        canView: perms.canView,
        canEdit: perms.canEdit,
        canUpload: perms.canUpload,
        canAdmin: perms.canAdmin,
        canManageUsers: perms.canManageUsers,
        canDelete: perms.canDelete,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    throw new Error("usePermissions must be used within a PermissionsProvider");
  }
  return ctx;
}

export default PermissionsContext;